import { useState } from "react";
import axios from "axios";
import { CheckCircle, Calendar, EnvelopeSimple, User, Buildings, Phone, ChatCircleText } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MEETING_TYPES = [
  { value: "discovery",             label: "Discovery call — first chat about a new project" },
  { value: "project_consult",       label: "Project consult — pricing, timeline, scope" },
  { value: "revision_review",       label: "Revision review — walk through feedback" },
  { value: "delivery_walkthrough",  label: "Delivery walkthrough — final files + handoff" },
  { value: "quote_request",         label: "Quote request — I know what I want, send me a number" },
  { value: "other",                 label: "Something else" },
];

/** Standalone (unlisted) meeting request form. Reachable only via direct link
 *  Vision Grafix admins share by email. Own header/shell — no login, no site nav. */
export default function MeetingRequest() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    meeting_type: "discovery",
    preferred_date: "", preferred_time: "",
    topic: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.topic.trim()) {
      return toast.error("Name, email, and topic are required");
    }
    if (!form.email.includes("@")) return toast.error("That doesn't look like a valid email");
    setSending(true);
    try {
      await axios.post(`${BACKEND}/api/public/meeting-request`, {
        ...form,
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        preferred_date: form.preferred_date || null,
        preferred_time: form.preferred_time.trim() || null,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-[#262626] px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-[#FF2D2D] font-display text-xl">VISION</div>
            <div className="font-display text-xl">GRAFIX</div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#666] hidden sm:block">Meeting request</div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-14">
        {done ? (
          <div className="border border-[#28C878] bg-gradient-to-b from-[#28C878]/10 to-transparent p-10 text-center" data-testid="meeting-success">
            <CheckCircle size={48} weight="fill" className="text-[#28C878] mx-auto mb-4" />
            <h1 className="font-display text-4xl uppercase">Got it.</h1>
            <p className="text-[#a3a3a3] mt-3 max-w-md mx-auto">
              I&apos;ll reach out within one business day to lock in a time. Check your inbox for a confirmation.
            </p>
            <div className="mt-8 text-[10px] uppercase tracking-[0.4em] text-[#666]">— Vision Grafix</div>
          </div>
        ) : (
          <>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Book a meeting</div>
            <h1 className="font-display text-5xl md:text-6xl mt-2">Let&apos;s talk.</h1>
            <p className="text-[#a3a3a3] mt-3 max-w-xl">
              Fill this out and I&apos;ll follow up within one business day to lock in a time.
              The more you share, the faster I can help.
            </p>

            <form onSubmit={submit} className="mt-10 space-y-6" data-testid="meeting-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Your name" required icon={User}>
                  <input required value={form.name} onChange={(e) => update("name", e.target.value)}
                    data-testid="meeting-name" maxLength={200}
                    className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-white focus:border-[#FF2D2D] outline-none" />
                </Field>
                <Field label="Email" required icon={EnvelopeSimple}>
                  <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
                    data-testid="meeting-email" maxLength={200}
                    className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-white focus:border-[#FF2D2D] outline-none" />
                </Field>
                <Field label="Company (optional)" icon={Buildings}>
                  <input value={form.company} onChange={(e) => update("company", e.target.value)}
                    data-testid="meeting-company" maxLength={200}
                    className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-white focus:border-[#FF2D2D] outline-none" />
                </Field>
                <Field label="Phone (optional)" icon={Phone}>
                  <input value={form.phone} onChange={(e) => update("phone", e.target.value)}
                    data-testid="meeting-phone" maxLength={40}
                    className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-white focus:border-[#FF2D2D] outline-none" />
                </Field>
              </div>

              <Field label="What do you want to talk about?" icon={ChatCircleText}>
                <select value={form.meeting_type} onChange={(e) => update("meeting_type", e.target.value)}
                  data-testid="meeting-type"
                  className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-white focus:border-[#FF2D2D] outline-none">
                  {MEETING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Preferred date (optional)" icon={Calendar}>
                  <input type="date" value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)}
                    data-testid="meeting-date"
                    className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-white focus:border-[#FF2D2D] outline-none" />
                </Field>
                <Field label="Preferred time (optional)">
                  <input value={form.preferred_time} onChange={(e) => update("preferred_time", e.target.value)}
                    data-testid="meeting-time" maxLength={100}
                    placeholder="e.g. afternoon EST, evenings only, weekends"
                    className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-white focus:border-[#FF2D2D] outline-none" />
                </Field>
              </div>

              <Field label="Topic / notes" required>
                <textarea required value={form.topic} onChange={(e) => update("topic", e.target.value)}
                  rows={5} maxLength={2000}
                  data-testid="meeting-topic"
                  placeholder="What are you building? Any references, deadlines, budget range, or existing brand? The more context the better."
                  className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-white focus:border-[#FF2D2D] outline-none" />
                <div className="text-[10px] text-[#666] mt-1 text-right">{form.topic.length}/2000</div>
              </Field>

              <button type="submit" disabled={sending} data-testid="meeting-submit"
                className="btn-primary w-full px-6 py-4 text-sm font-bold uppercase tracking-wider disabled:opacity-50">
                {sending ? "Sending…" : "Send request"}
              </button>
              <div className="text-[10px] text-[#666] text-center">
                By submitting, you agree to be contacted at the email you provided. That&apos;s it.
              </div>
            </form>
          </>
        )}
      </main>
    </div>
  );
}

function Field({ label, required, icon: Icon, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2 flex items-center gap-1.5">
        {Icon && <Icon size={11} weight="bold" />}
        {label} {required && <span className="text-[#FF2D2D]">*</span>}
      </label>
      {children}
    </div>
  );
}
